import {createSlice, createSelector, PayloadAction} from '@reduxjs/toolkit'
import {RootState} from '../../store/Store'
import {ticketApi} from './Ticket.api'
import { IFilterDto, TSort } from '../../../common/model/Core.model'
import { IATicket, ITicketDto } from './Ticket.model'
import { signOut } from '../../auth/Auth.slice'
import { TimeRangeItem } from '../../../util/Constants'
import { TimeRange } from '../../../common/component/DateRangeSelector'
import { recalibrateTimeRange } from '../../../util/TimeRange.util'

export type SerializableTimeRange = {
  timeRangeItem: TimeRangeItem
  from: number
  to: number
}

type InitialState = {
  timeRangeSelection: SerializableTimeRange
  filtersSelection: IFilterDto[]
  tickets: {
    error?: string
    loading: boolean
    data: ITicketDto[]
    pagination?: {
      limit?: number
      sort?: TSort
      page?: string | undefined
    }
    filters?: IFilterDto[]
  }
  ticket: {
    error?: string
    loading: boolean
    data?: IATicket
  }
}
const initialState = {
  timeRangeSelection: {
    timeRangeItem: { code: '30d', name: 'ÚLTIMOS 30 DÍAS', metric: 'D' as const, value: 30 },
    from: Date.now() - 30 * 24 * 60 * 60 * 1000,
    to: Date.now(),
  },
  filtersSelection: [] as IFilterDto[],
  tickets: {
    error: undefined,
    loading: false,
    data: [],
    pagination: {
      limit: 25,
      sort: 'ASC',
      page: undefined,
    },
    filters: undefined,
  },
  ticket: {
    error: undefined,
    loading: false,
    data: undefined,
  },
} as InitialState

export const ticketSlice = createSlice({
  name: 'ticket',
  initialState,
  reducers: {
    setTicketTimeRangeSelection: (state, action: PayloadAction<SerializableTimeRange>) => {
      state.timeRangeSelection = action.payload
    },
    setTicketFiltersSelection: (state, action: PayloadAction<IFilterDto[]>) => {
      state.filtersSelection = action.payload
    },
  },
  extraReducers(builder) {
    builder
      .addCase(signOut, () => initialState)
      //Search
      .addMatcher(ticketApi.endpoints.ticketSearch.matchPending, (state) => {
        state.tickets = {
          ...initialState.tickets,
          error: undefined,
          loading: true,
        }
      })
      .addMatcher(ticketApi.endpoints.ticketSearch.matchFulfilled, (state, action) => {
        state.tickets = {
          error: undefined,
          loading: false,
          data: action.payload.data,
          pagination: action.payload.pagination,
          filters: action.payload.filters,
        }
      })
      .addMatcher(ticketApi.endpoints.ticketSearch.matchRejected, (state, action) => {
        state.tickets = {
          ...state.tickets,
          error: action.error.message,
          loading: false,
        }
      })
      //LoadMore
      .addMatcher(ticketApi.endpoints.ticketLoadMore.matchPending, (state) => {
        state.tickets = {
          ...state.tickets,
          error: undefined,
          loading: true,
        }
      })
      .addMatcher(ticketApi.endpoints.ticketLoadMore.matchFulfilled, (state, action) => {
        state.tickets = {
          ...state.tickets,
          error: undefined,
          loading: false,
          data: state.tickets.data.concat(action.payload.data),
          pagination: action.payload.pagination,
          filters: action.payload.filters,
        }
      })
      .addMatcher(ticketApi.endpoints.ticketLoadMore.matchRejected, (state, action) => {
        state.tickets = {
          ...state.tickets,
          error: action.error.message,
          loading: false,
        }
      })
      //LoadMore
      // .addMatcher(ticketApi.endpoints.ticketLoadMore.matchPending, (state) => {
      //   state.tickets = {
      //     ...state.tickets,
      //     error: undefined,
      //     loading: true,
      //   }
      // })
      // .addMatcher(ticketApi.endpoints.ticketLoadMore.matchFulfilled, (state, action) => {
      //   state.tickets = {
      //     ...state.tickets,
      //     error: undefined,
      //     loading: false,
      //   }
      // })
      // .addMatcher(ticketApi.endpoints.ticketLoadMore.matchFulfilled, (state, action) => {
      //   state.tickets = {
      //     ...state.tickets,
      //     error: undefined,
      //     loading: false,
      //     data: state.tickets.data.concat(action.payload.data),
      //     pagination: action.payload.pagination,
      //     filters: action.payload.filters,
      //   }
      // })
      // .addMatcher(ticketApi.endpoints.ticketLoadMore.matchRejected, (state, action) => {
      //   state.tickets = {
      //     ...state.tickets,
      //     error: action.error.message,
      //     loading: false,
      //   }
      // })
      //GetById
      .addMatcher(ticketApi.endpoints.ticketGetById.matchPending, (state) => {
        state.ticket = {
          ...initialState.ticket,
          error: undefined,
          loading: true,
          data: undefined,
        }
      })
      .addMatcher(ticketApi.endpoints.ticketGetById.matchFulfilled, (state, action) => {
        state.ticket = {
          error: undefined,
          loading: false,
          data: action.payload,
        }
      })
      .addMatcher(ticketApi.endpoints.ticketGetById.matchRejected, (state, action) => {
        state.ticket = {
          ...initialState.ticket,
          error: action.error.message,
          loading: false,
        }
      })
      //Create
      .addMatcher(ticketApi.endpoints.ticketCreate.matchPending, (state) => {
        state.ticket = {
          ...initialState.ticket,
          error: undefined,
          loading: true,
          data: undefined,
        }
      })
      .addMatcher(ticketApi.endpoints.ticketCreate.matchFulfilled, (state, action) => {
        state.ticket = {
          ...state.ticket,
          error: undefined,
          loading: false,
          data: action.payload,
        }
      })
      .addMatcher(ticketApi.endpoints.ticketCreate.matchRejected, (state, action) => {
        state.ticket = {
          ...initialState.ticket,
          error: action.error.message,
          loading: false,
        }
      })
      //DeleteById
      .addMatcher(ticketApi.endpoints.ticketDeleteById.matchPending, (state) => {
        state.ticket = {
          ...initialState.ticket,
          error: undefined,
          loading: true,
        }
      })
      .addMatcher(ticketApi.endpoints.ticketDeleteById.matchFulfilled, (state, action) => {
        state.ticket = {
          ...state.ticket,
          error: undefined,
          loading: false,
        }
      })
      .addMatcher(ticketApi.endpoints.ticketDeleteById.matchRejected, (state, action) => {
        state.ticket = {
          ...initialState.ticket,
          error: action.error.message,
          loading: false,
        }
      })
      //UpdateById
      .addMatcher(ticketApi.endpoints.ticketUpdateById.matchPending, (state) => {
        state.ticket = {
          ...initialState.ticket,
          error: undefined,
          loading: true,
        }
      })
      .addMatcher(ticketApi.endpoints.ticketUpdateById.matchFulfilled, (state, action) => {
        state.ticket = {
          error: undefined,
          loading: false,
          data: action.payload,
        }
      })
      .addMatcher(ticketApi.endpoints.ticketUpdateById.matchRejected, (state, action) => {
        state.ticket = {
          ...initialState.ticket,
          error: action.error.message,
          loading: false,
        }
      })
  },
})

export const {setTicketTimeRangeSelection, setTicketFiltersSelection} = ticketSlice.actions
export default ticketSlice.reducer
export const selectTicketsError = (state: RootState) => state.ticketReducer.tickets.error
export const selectTicketsLoading = (state: RootState) => state.ticketReducer.tickets.loading
export const selectTickets = (state: RootState) => state.ticketReducer.tickets.data
export const selectTicketsPagination = (state: RootState) => state.ticketReducer.tickets.pagination
export const selectTicketsFilters = (state: RootState) => state.ticketReducer.tickets.filters

export const selectTicketError = (state: RootState) => state.ticketReducer.ticket.error
export const selectTicketLoading = (state: RootState) => state.ticketReducer.ticket.loading
export const selectTicket = (state: RootState) => state.ticketReducer.ticket.data

const selectTicketTimeRangeSelectionState = (state: RootState) => state.ticketReducer.timeRangeSelection
export const selectTicketTimeRangeSelection = createSelector(
  [selectTicketTimeRangeSelectionState],
  (timeRangeSelection): TimeRange => recalibrateTimeRange(timeRangeSelection)
)
export const selectTicketFiltersSelection = (state: RootState) => state.ticketReducer.filtersSelection
